import { useMe, usePopUpMessage } from '@hb/react'
import { FieldMapValue, isPracticeContract, PracticeContract, WithId } from '@hb/shared'
import { useCallback, useMemo, useState } from 'react'
import { signPracticeContract } from '../backend/practiceContracts/utils'

export const useSignPracticeContract = (
  document: WithId<PracticeContract> | null,
  onSignSuccess?: () => void,
  adminView?: boolean,
) => {
  const { processResponse } = usePopUpMessage()
  const [isSigning, setIsSigning] = useState(false)
  const me = useMe()

  const handleSignPracticeContract = useCallback(
    async (formData: FieldMapValue) => {
      if (!document) return processResponse({ error: 'No document selected' })
      try {
        setIsSigning(true)
        await signPracticeContract({
          practiceId: document.practiceId,
          practiceContractId: document.id,
          formData,
        })
      } catch (err: any) {
        console.error(err)
        setIsSigning(false)
        return processResponse({
          error: err?.message || 'Error signing practice contract',
        })
      }
      setIsSigning(false)
      if (onSignSuccess) onSignSuccess()
      return processResponse({ success: 'Practice contract signed successfully' })
    },
    [processResponse, document, onSignSuccess],
  )

  const onSignContract = useMemo(
    () =>
      me &&
      document &&
      isPracticeContract(document) &&
      !document.signedBy &&
      me.uid === document.toBeSignedByUser
        ? handleSignPracticeContract
        : undefined,
    [me, document, handleSignPracticeContract],
  )

  const canDownload = useMemo(() => {
    if (!document) return false
    if (document.signedOn) return true
    if (adminView) return true
    return false
  }, [document, adminView])

  return { isSigning, onSignContract, canDownload }
}
